import { Typography, message, Input, Row, Col, Card, Table, Tag, Space, Button, Grid, Tooltip, Dropdown, Menu } from "antd";
import { DatabaseOutlined, SearchOutlined, ExportOutlined, MoreOutlined } from "@ant-design/icons";
import { useState, useEffect, useRef } from "react";
import axios from "axios";

const { useBreakpoint } = Grid;

const headerMap = {
  Nacionalidad: "Nacionalidad",
  ObjetoSolicitud: "Objeto de la solicitud",
  ClasificacionSCIAN: "Clasificación SCIAN",
  TipoConcentracion: "Tipo de concentración",
  TipoDato_Corto: "Tipo de identificación",
  TipoDato_Largo: "Descripción larga",
  DocumentoOrigen: "Documento de origen",
  ExpedidoPor: "Expedido por",
  TipoInstalacion: "Tipo de instalación",
  TipoMod: "Tipo de modificación",
  TipoPersona: "Tipo de persona",
  TipoTelefono: "Tipo de teléfono",
  TipoTension: "Tipo de tensión",
  TipoVerificacion: "Tipo de verificación",
};

const longColumns = ["ClasificacionSCIAN", "TipoConcentracion", "TipoInstalacion", "TipoVerificacion", "TipoDato_Largo"];

const CARD = {
  background: "#fff",
  border: "0.5px solid rgba(0,0,0,0.08)",
  borderRadius: "12px",
};

export default function CatalogView({ tableName, title, icon }) {
  const [data, setData] = useState([]);
  const [currentData, setCurrentData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [pk, setPk] = useState("");
  const searchInput = useRef(null);
  const screens = useBreakpoint();

  const fetchCatalog = () => {
    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_URL}/catalogos/${tableName}`)
      .then((res) => {
        setColumns(res.data.columns);
        setData(res.data.data);
        setFilteredData(res.data.data);
        setCurrentData(res.data.data);
        setPk(res.data.pk);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        message.error(err.message);
      });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCatalog();
  }, [tableName]);

  useEffect(() => {
    if (search) {
      const filtered = data.filter((item) => {
        return Object.values(item).some(val => 
          val && val.toString().toLowerCase().includes(search.toLowerCase())
        );
      });
      setFilteredData(filtered);
      setCurrentData(filtered);
    } else {
      setFilteredData(data);
      setCurrentData(data);
    }
  }, [search, data]);

  const handleExport = () => {
    if (!currentData?.length) return message.warning("No hay datos para exportar");

    const displayColumns = columns.map(col => headerMap[col] || col);
    const headers = [...displayColumns, "Verificaciones"];

    const escapeCSV = (val) => {
      if (val === null || val === undefined) return "";
      const s = String(val);
      if (s.includes(",") || s.includes('"') || s.includes("\n")) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const rows = currentData.map((v) => {
      const rowData = columns.map(col => escapeCSV(v[col]));
      rowData.push(escapeCSV(v.VerificacionesCount));
      return rowData;
    });

    const csvContent = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${tableName}_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    message.success("Exportado correctamente");
  };

  const getColumnSearchProps = (dataIndex, colTitle) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Buscar ${colTitle}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Buscar
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reiniciar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
    onFilter: (value, record) =>
      record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : "",
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const tableColumns = columns.map(col => {
    const displayTitle = headerMap[col] || col;
    const isLong = longColumns.includes(col);
    
    return {
      title: <span style={{ fontWeight: 700, color: "#1890ff" }}>{displayTitle}</span>,
      dataIndex: col,
      key: col,
      ellipsis: isLong ? { showTitle: true } : false,
      width: isLong ? 300 : 180,
      sorter: (a, b) => (a[col] || "").toString().localeCompare((b[col] || "").toString()),
      ...getColumnSearchProps(col, displayTitle),
      render: (text) => text || <span className="subtle-text">N/D</span>
    };
  });

  tableColumns.push({
    title: <span style={{ fontWeight: 700, color: "#1890ff" }}>Verificaciones</span>,
    dataIndex: "VerificacionesCount",
    key: "VerificacionesCount",
    align: "center",
    width: 150,
    render: (text) => (
      <Tag color={text > 0 ? "blue" : "default"} style={{ borderRadius: "10px", fontWeight: "bold" }}>
        {text || 0}
      </Tag>
    ),
    sorter: (a, b) => a.VerificacionesCount - b.VerificacionesCount,
    filters: [
      { text: "Con verificaciones", value: "active" },
      { text: "Sin verificaciones", value: "inactive" },
    ],
    onFilter: (value, record) => (value === "active" ? record.VerificacionesCount > 0 : record.VerificacionesCount === 0),
  });

  const mobileMenuItems = [
    {
      key: "exportar",
      label: "Exportar",
      icon: <ExportOutlined />,
      onClick: handleExport,
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      {/* ── Header card ── */}
      <div style={{ ...CARD, padding: 0 }}>
        {/* Title row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "14px",
            padding: "18px 20px 14px",
            borderBottom: "0.5px solid rgba(0,0,0,0.06)",
            flexWrap: "wrap",
          }}
        >
          {/* Icon badge */}
                      <div
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: 10,
                          background: "rgba(24,144,255,0.08)",
                          border: "0.5px solid rgba(24,144,255,0.18)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {icon || <DatabaseOutlined style={{ fontSize: 17, color: "#1677ff" }} />}
                      </div>
                    {/* Title + subtitle */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "rgba(0,0,0,0.88)", lineHeight: 1.3 }}>{title}</div>
            <div style={{ fontSize: 12, color: "rgba(0,0,0,0.38)", marginTop: 1 }}>Catálogo del sistema para consulta de datos</div>
          </div>

          {/* Desktop actions */}
          <Space size={8} className="vf-desktop-actions" style={{ flexShrink: 0 }}>
            <Tooltip title="Exportar como CSV">
              <Button icon={<ExportOutlined />} style={{ fontSize: 13 }} onClick={handleExport}>
                Exportar
              </Button>
            </Tooltip>
          </Space>

          {/* Mobile actions */}
          <Space size={8} className="vf-mobile-actions" style={{ flexShrink: 0 }}>
            <Dropdown overlay={<Menu items={mobileMenuItems} />} trigger={["click"]} placement="bottomRight">
              <Button icon={<MoreOutlined />} />
            </Dropdown>
          </Space>
        </div>

        {/* Search row */}
        <div style={{ padding: "12px 20px" }}>
          <Input.Search
            placeholder="Filtro general…"
            onSearch={(value) => setSearch(value)}
            onChange={(e) => {
              if (!e.target.value) setSearch("");
            }}
            allowClear
            loading={loading}
            style={{ maxWidth: 380 }}
          />
        </div>
      </div>

      {/* ── Table card ── */}
      <div style={{ ...CARD, overflow: "hidden", fontSize: "13px" }}>
        <Table
          columns={tableColumns}
          dataSource={filteredData}
          loading={loading}
          rowKey={pk || "id"}
          size="small"
          onChange={(pagination, filters, sorter, extra) => {
            setCurrentData(extra.currentDataSource);
          }}
          pagination={{
            defaultPageSize: 25,
            showSizeChanger: true,
            showLessItems: true,
            pageSizeOptions: ["10", "25", "50", "100"],
            position: ['bottomCenter']
          }}
          scroll={!screens.lg ? { x: 'max-content' } : null}
          style={{ padding: "8px" }}
        />
      </div>

      {/* Responsive */}
      <style>{`
        .vf-mobile-actions  { display: none !important; }
        .vf-desktop-actions { display: flex !important; }
        @media (max-width: 600px) {
          .vf-mobile-actions  { display: flex !important; }
          .vf-desktop-actions { display: none !important; }
        }
      `}</style>
    </div>
  );
}
